'use strict';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CurationEditorHelper } from '../../../curation-editor-helper';
import { get, set } from 'lodash';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { ExternalSearchService } from '@ispm/shared/services/external-search.service';
import { ICurationItem } from '../../../../../interfaces/curation-item.interface';
import { CurationUtils } from '../../../../../utils/curation-utils';
import { CurationSettings } from '../../../../curation-dashboard/curation-setting';

@Component({
  selector: 'gen-curation-editor-items-container',
  templateUrl: 'curation-editor-items-container.component.html',
  styleUrls: ['curation-editor-items-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class CurationEditorItemsContainerComponent implements OnInit {
  @Output() validated: EventEmitter<any> = new EventEmitter();
  @Output() itemDeleted: EventEmitter<{ root: string, key: string}> = new EventEmitter();
  @Output() itemAdded: EventEmitter<any> = new EventEmitter();
  @Input() readOnly: boolean;
  @Input() rootType: string;
  @Input() formName: string;
  @Input() title: string;
  @Input() itemTitle: string;
  @Input() vertical: boolean;
  @Input() tableVertical: boolean; // indicates if to display the form items in 2 separate columns within a single table's row
  @Input() viewAsTable: boolean;  // indicates if to display the form items in a table view (each field is a separate column) or that each label-field is in a separate row
  @Input() subItem: boolean = false; // for clinical sig sub items
  @Input() filterValue: string; // for clinical sig - filtering according to 'type'
  @Input() filterParam: string;
  @Input() hiddenFieldName: string; // for clinical sig - adding 'type' when creating new
  @Input() hiddenFieldDisplay: string;
  @Input() showAddButton: boolean = true;
  @Input() showDeleteButton: boolean = true;
  @Input() prioritizeAvailable = false;
  @Input() listenToDataChange = true;
  @Input() disableButton: boolean = false;
  @Input() revisionTag: string;
  @Input() submitted: boolean = false;
  @Input() approvedMode: boolean = false;
  @Input() searchable: boolean = false;
  @Input() curationItem: ICurationItem;
  @Input() searchDataConfigParams: any;
  @Input() googleSearchURL: string;
  @Input() pubmedSearchURL: string;

  private _curationData: any = { };
  public filteredData: any;
  public currentLink: string;
  public prioritizeTrackByField: string = CurationEditorHelper.INTERNAL_KEY_PARAM_NAME;
  public schema = NewCurationItem;

  constructor(private externalSearchService: ExternalSearchService) { }

  ngOnInit() { }

  @Input() set curationData(data) {
    if (data) {
      this._curationData = data;
      this.filterData();
    }
  }

  get curationData(): any {
    return this._curationData;
  }

  filterData() {
    this.filteredData = get(this._curationData, this.rootType);
    if (this.filteredData && this.filterValue && this.filterParam) {
      this.filteredData = this.filteredData.filter(item => get(item, this.filterParam) == this.filterValue);
    }
  }

  updateContainedData(dataArray: any[]) {
    // dataArray contains all tables information and not just the table that was changed
    dataArray.forEach((table) => { // remove empty values from the object
      for (const prop in table) {
        if (table[prop] === null || table[prop] === undefined || table[prop] === '') {
          delete table[prop];
        }
      }
    });

    // Clinical significance has 3 types.
    // Look for the type that was changed and remove all clinical significances of this type.
    if (this.rootType == CurationEditorHelper.CLINICAL_SIG_ROOT) {
      // filterValue - is the current Clinical significance type.
      // Delete all existing items of this type and adding the items from dataArray.
      if (this._curationData.clinicalSignificances) {
        for (let i = this._curationData.clinicalSignificances.length - 1; i >= 0 ; i--) { // remove empty values from the object
          if (this._curationData.clinicalSignificances[i].significanceType && this._curationData.clinicalSignificances[i].significanceType.code) {
            if (this._curationData.clinicalSignificances[i].significanceType.code == this.filterValue) {
              this._curationData.clinicalSignificances.splice(i, 1);
            }
          }
        }

        dataArray = this.pubmedUpdate(dataArray);
      }
      else {
        this._curationData.clinicalSignificances = [];
      }

      dataArray.forEach((clinicalSig) => this._curationData.clinicalSignificances.push(clinicalSig));
    }
    else {
      set(this._curationData, this.rootType, dataArray);
    }

    this.curationData = this._curationData;
  }

  /**
   * this method is invoked from DynamicFormTableComponent.
   */
  addNewItem(eventData) {
    if (! eventData) { return; }
    const newObj = eventData.item;
    if (this.hiddenFieldName) {
      newObj[this.hiddenFieldName] = { };
      const obj = { code: this.filterValue, display: this.filterValue};
      newObj[this.hiddenFieldName] = obj;
    }
    this.updateContainedData(eventData.dataArray);
    this.itemAdded.emit(newObj);
  }

  /**
   * this method is invoked from DynamicFormTableComponent.
   */
  deleteItem(eventData) {

    if (! eventData) { return; }

    const deletedItem = eventData.item;

    // if item was deleted already from the list, it is required to update the curationData only.
    // if item was NOT deleted (as when deleting an item that was saved before) - mark it as 'deleted' and update the curationData
    if (! this.shouldRemoveItemOnDelete(deletedItem)) {
      deletedItem[CurationSettings.TABLE_TAG_DELETED] = true;
      deletedItem[CurationSettings.TABLE_TAG_DEL_REV_ID] = this.updatedItemFieldsContext(deletedItem);
    }

    this.updateContainedData(eventData.dataArray);
    this.itemDeleted.emit({ root: this.rootType, key: deletedItem[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME]});
  }

  /**
   * indicate if to remove an item from the list, when Delete action is activated
   */
  public shouldRemoveItemOnDelete(itemDetails) {
    return itemDetails &&
      itemDetails[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME];
  }

  updateLink(searchType: string) {
    let template: string = '';
    if (this.searchDataConfigParams && this.searchDataConfigParams['clinicalSignificanceSearchTemplate'] && this.searchDataConfigParams['clinicalSignificanceSearchTemplate'][this.title]) {
      template = this.searchDataConfigParams['clinicalSignificanceSearchTemplate'][this.title];
      if (template == '') {
        console.error('CurationEditorItemsContainerComponent->updateLink cannot find clinicalSignificanceSearchTemplate for ' + this.title);
        return;
      }
    }
    const url: string = searchType.toLowerCase() == 'google' ? this.googleSearchURL : this.pubmedSearchURL;
    this.currentLink = this.externalSearchService.getSearchQueryByTemplate(url, this.searchDataConfigParams,
        CurationUtils.getFlatAberrationData(this.curationItem), template);
  }

  /**
   * return a reference to a function the calculate the fields' context of a given item.
   *
   * Note: since that function is invoked from DynamicFormTableComponent.getItemUpdateContext(), it is required to
   *       bind the function to the CurationEditorItemsContainerComponent's this instance.
   */
  public updatedItemFieldsContextRef = (itemDetails) => {
    return this.updatedItemFieldsContext(itemDetails);
  }

  public updatedItemFieldsContext (itemDetails) {
    return this.approvedMode ? null : this.revisionTag + '__' + (itemDetails ? itemDetails[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME] : undefined);
  }

  public itemValidationEventRef = (valid: boolean, itemDetails) => {
    if (! itemDetails || this.isItemDisabled(itemDetails)) { // do not validate readonly forms
      return;
    }
    this.validated.emit({
      elementKey: itemDetails[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME],
      validationStatus: valid
    });
  }

  public getCurationItemNgClass(itemDetails) {
    const bool: boolean = !this.approvedMode &&
      itemDetails &&
      itemDetails[CurationSettings.TABLE_TAG_DEL_REV_ID] !== undefined &&
      itemDetails[CurationSettings.TABLE_TAG_DEL_REV_ID] === this.updatedItemFieldsContext(itemDetails);

    return { 'diff_marker': bool, 'dynamic-form-item-deleted': this.isItemMarkedDeleted(itemDetails)};
  }

  isReadOnlyMode() {
    return this.readOnly && this.readOnly === true;
  }

  isItemMarkedDeleted (item): boolean {
    return (item &&
      item[CurationSettings.TABLE_TAG_DELETED] &&
      item[CurationSettings.TABLE_TAG_DELETED] === true);
  }

  public isItemDisabled(item): boolean {
    return this.isItemMarkedDeleted(item) || this.isReadOnlyMode();
  }

  public isItemEditMode(item): boolean {
    if (! item) {
      return false;
    }
    const newItem = item[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME] &&
      item[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME] === true;
    const addNewMode = item[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME] && newItem;
    return !(newItem || addNewMode);
  }

  public isItemDeletable(): boolean {
    return true;
  }

  public itemListenToDataChange(): boolean {
    return true;
  }

  public itemUpdateOnBlur(): boolean {
    return false;
  }

  private pubmedUpdate(items: any) {
    items.forEach((item, idx) => {
      if (!item.hasOwnProperty('pubmedLinks')) {
        return;
      }

      items[idx].pubmedLinks = CurationUtils.removeDuplicatePubmed(item.pubmedLinks);
    });

    return items;
  }
}

class NewCurationItem {
  constructor() {
    this[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME] = true;
    this[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME] = StringUtils.generateGuid();
  }
}
